// role-based-dashboards — Phase 6.2: ungated entity-count strip widget.
//
// Simple counts of users/locations/events/members — no advancedAnalytics
// gating (spec "ungated widgets render without advancedAnalytics"). Each
// count is an independent query so one failing endpoint never blocks the
// others (spec "unrelated widget error isolation").

import { listEvents, listLocations, listMembers, listUsers } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import { StatCard } from '@jq4race/ui';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, IdCard, MapPin, Users as UsersIcon } from 'lucide-react';

export function EntityCountStrip() {
  const { t } = useTranslation();
  const usersQuery = useQuery({
    queryKey: ['dashboard-overview', 'users-count'],
    // BUGFIX (admin-users-list-query-validation) — ListUsersQuerySchema
    // rejects `page`/`limit` unless BOTH are provided together (avoids
    // ambiguous pagination math on the backend); `limit` alone used to throw
    // an uncaught ZodError there (raw 500). Only `total` is read here (a
    // count widget), so `limit: 1` still minimizes the payload — it just
    // needs a `page` alongside it.
    queryFn: () => listUsers({ page: 1, limit: 1 }),
  });
  const locationsQuery = useQuery({
    queryKey: ['dashboard-overview', 'locations-count'],
    queryFn: () => listLocations(),
  });
  const eventsQuery = useQuery({
    queryKey: ['dashboard-overview', 'events-count'],
    queryFn: () => listEvents(),
  });
  const membersQuery = useQuery({
    queryKey: ['dashboard-overview', 'members-count'],
    queryFn: () => listMembers(),
  });

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={UsersIcon}
        label={t('entities.users')}
        value={usersQuery.isError ? '—' : usersQuery.data ? usersQuery.data.total.toString() : null}
      />
      <StatCard
        icon={MapPin}
        label={t('entities.locations')}
        value={
          locationsQuery.isError
            ? '—'
            : locationsQuery.data
              ? locationsQuery.data.locations.length.toString()
              : null
        }
      />
      <StatCard
        icon={CalendarDays}
        label={t('entities.events')}
        value={
          eventsQuery.isError
            ? '—'
            : eventsQuery.data
              ? eventsQuery.data.events.length.toString()
              : null
        }
      />
      <StatCard
        icon={IdCard}
        label={t('entities.members')}
        value={
          membersQuery.isError
            ? '—'
            : membersQuery.data
              ? membersQuery.data.members.length.toString()
              : null
        }
      />
    </div>
  );
}
